import { useNavigation, useNavigationState } from '@react-navigation/native';
import { fontSizes } from '_/config/theme';
import Amenities from '_/space-management/components/Amenities';
import Button from '_/space-management/components/Button';
import DropdownButton from '_/space-management/components/DropdownButton';
import ModalConfirmation from '_/space-management/components/ModalConfirmation';
import Text from '_/space-management/components/Text';
import TimePicker from '_/space-management/components/TimePicker';
import Title from '_/space-management/components/Title';
import { DAY_MONTH_FORMAT, TIME_FORMAT, WEEK_DAY_FORMAT } from '_/space-management/config/date';
import { colors, spacing } from '_/space-management/constants/theme';
import { formatDate } from '_/space-management/helpers/formatDate';
import { useEventContext } from '_/space-management/hooks/EventContext';
import { AppRoute } from '_/space-management/navigation/types';
import { EventStatus } from '_/space-management/services/models/enums/event-status.enum';
import { EventsResponse } from '_/space-management/services/models/events.model';
import { parseISO } from 'date-fns';
import { lighten } from 'polished';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, StyleSheet, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import Modal from 'react-native-modal';

interface ReservationModalProps {
  event: EventsResponse;
  visible: boolean;
  setCancelModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onBackdropPress: () => void;
  onBackButtonPress: () => void;
}

export default function ReservationModal({
  event,
  visible,
  setCancelModalVisible,
  onBackdropPress,
  onBackButtonPress,
}: ReservationModalProps) {
  const { cancelEvent, cancelRecurrenceEvent, loading, setLoading } = useEventContext();
  const navigation = useNavigation<any>();
  const currentScreen = useNavigationState((state) => state.routes[state.index]?.name);
  const startAtToDateType = Date.parse(event.startAt);
  const endAtToDateType = Date.parse(event.endAt);
  const selectedStartTime = formatDate(startAtToDateType, TIME_FORMAT);
  const selectedEndTime = formatDate(endAtToDateType, TIME_FORMAT);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  const handleCancelEvent = async () => {
    setCancelModalVisible(false);
    await cancelEvent(event.id);
  };

  async function handleCancelEventRecurrences() {
    setCancelModalVisible(false);
    await cancelRecurrenceEvent(event.recurrenceId);
    navigation.navigate(AppRoute.HOME);
  }

  const handleGoToRecurringReservations = async () => {
    navigation.navigate(AppRoute.RECURRING_RESERVATIONS, {
      recurrenceId: event.recurrenceId,
      recurrenceString: event.recurrenceString,
    });
    setCancelModalVisible(false);
  };

  const onCloseModal = () => {
    setIsVisible(false);
    setLoading(false);
  };

  const onConfirmModal = () => {
    setLoading(true);
    handleCancelEventRecurrences();
  };

  return (
    <KeyboardAvoidingView enabled behavior={Platform.OS === 'ios' ? 'position' : undefined}>
      <ScrollView>
        <Modal
          style={styles.modal}
          isVisible={visible}
          onBackdropPress={onBackdropPress}
          onBackButtonPress={onBackButtonPress}
          coverScreen
          hasBackdrop
          backdropOpacity={0.5}
        >
          <Title
            style={styles.title}
            title={event.slot?.name}
            hasCloseButton
            onPress={onBackButtonPress}
          />
          <Amenities style={styles.amenities} />
          <View style={styles.buttonGroup}>
            <DropdownButton
              icon="user"
              text={t('spaceManage.reservationsScreen.cancelReservationModal.forMe')}
              disabled
            />
            <DropdownButton icon="rotate-cw" text={t('spaceManage.recurrenceTypes.ONCE')} disabled />
          </View>
          <Text fontSize={fontSizes.lg} fontType="bold" style={styles.text}>
            {formatDate(startAtToDateType, WEEK_DAY_FORMAT)}
            , {formatDate(startAtToDateType, DAY_MONTH_FORMAT)}
          </Text>
          <TimePicker
            editable={false}
            defaultSelectedStartTime={selectedStartTime}
            defaultSelectedEndTime={selectedEndTime}
            selectedNewTime={{ start: parseISO(event.startAt), end: parseISO(event.endAt) }}
            hiddenError
          />
          <View style={styles.buttonGroup}>
            {event.recurrenceId && currentScreen !== AppRoute.RECURRING_RESERVATIONS && (
              <Button
                backgroundColor={lighten(0.57, colors.lightBlue)}
                textColor={colors.lightBlue}
                onPress={handleGoToRecurringReservations}
              >
                {t('spaceManage.reservationsScreen.cancelReservationModal.seeRecurrence')}
              </Button>
            )}
            {(event.status === EventStatus.PENDING || event.status === EventStatus.CONFIRMED) && (
              <>
                {event.recurrenceId && currentScreen !== AppRoute.RECURRING_RESERVATIONS ? (
                  <>
                    {!event.slot?.space?.scheduleEnabled && (
                      <Button
                        backgroundColor={colors.lightBlue}
                        textColor={colors.white}
                        onPress={() => {
                          onBackdropPress();
                          navigation.navigate(AppRoute.EDIT_EVENT, {
                            event,
                          });
                        }}
                        loading={loading}
                      >
                        {t('spaceManage.editEventScreen.editEvent')}
                      </Button>
                    )}
                    <Button
                      backgroundColor={colors.lightRed}
                      textColor={colors.errorRed}
                      onPress={() => setIsVisible(true)}
                      loading={loading}
                    >
                      {t('spaceManage.reservationsScreen.cancelReservationModal.cancelRecurrenceButton')}
                    </Button>
                  </>
                ) : (
                  <>
                    {!event.slot?.space?.scheduleEnabled && (
                      <Button
                        backgroundColor={colors.lightBlue}
                        textColor={colors.white}
                        onPress={() => {
                          onBackdropPress();
                          navigation.navigate(AppRoute.EDIT_EVENT, {
                            event,
                          });
                        }}
                        loading={loading}
                      >
                        {t('spaceManage.editEventScreen.editEvent')}
                      </Button>
                    )}
                    <Button
                      backgroundColor={colors.lightRed}
                      textColor={colors.errorRed}
                      onPress={handleCancelEvent}
                      loading={loading}
                    >
                      {t('spaceManage.reservationsScreen.cancelReservationModal.cancelReservationButton')}
                    </Button>
                  </>
                )}
              </>
            )}
            <ModalConfirmation
              title={t('spaceManage.reservationsScreen.cancelReservationModal.cancelRecurrenceButton')}
              description={t('spaceManage.reservationsScreen.cancelReservationModal.cancelRecurrenceMessage')}
              isVisible={isVisible}
              onCloseModal={onCloseModal}
              onConfirmModal={onConfirmModal}
              onCancelButton={onCloseModal}
              disabledButtons={loading}
            />
          </View>
        </Modal>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  modal: {
    flex: 1,
    width: '100%',
    ...(Platform.OS === 'web' && { maxWidth: 768 }),
    margin: 0,
    marginTop: 80,
    alignSelf: 'center',
    justifyContent: 'flex-start',
    backgroundColor: '#FFF',
    borderTopRightRadius: 16,
    borderTopLeftRadius: 16,
    paddingTop: 32,
    paddingBottom: 32,
  },
  title: {
    marginTop: 0,
    marginBottom: spacing.lg,
  },
  buttonGroup: {
    marginHorizontal: spacing.md,
    marginBottom: 6,
  },
  text: {
    marginHorizontal: spacing.md,
    textTransform: 'capitalize',
    color: colors.darkGrey,
  },
  amenities: {
    paddingHorizontal: spacing.md,
    marginBottom: 24,
  },
});
